import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { listenerMiddleware } from "../middlewares/listener";
import { DEFAULT_MUSIC_VOLUME, DEFAULT_ATC_VOLUME } from "@/app/constants/appConstants";
import { Logger } from "@/app/utils/logger";
import { applyTheme } from "@helpers/applyTheme";
import { AppTheme } from "@settings/themes/types";
import { DEFAULT_ATC_RADIO_EFFECT_STRENGTH } from '@/types/AtcRadioEffect';


export type AtcStereoWidthMode = 'off' | 'medium' | 'wide';

export interface UserPreferencesState {
  selectedThemeName: string,
  themes: AppTheme[],
  likedAirportsIata: string[],
  musicVolume: number,
  atcVolume: number,
  atcStereoWidthMode: AtcStereoWidthMode,
  atcRadioEffectStrength: number,
  streakCount: number,
  lastVisitDate: string | null,
  sessionStartTime: number | null,
}

export const defaultTheme: AppTheme = {
  name: 'default',
  colors: {
    primary: '#12bedd',
    secondary: '#00ff00',
    background: '#0000ff',
    text: '#ffffff',
  },
  backgroundImageURL: 'https://cdn.midjourney.com/aeb9cae2-47e3-4edf-97b1-5170e511429f/0_0.png',
  grayscaleBackground: false,
  backgroundGradient: {
    enabled: false,
    colors: [],
    angle: 0
  }
}

export const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState: {
    selectedThemeName: 'default',
    themes: [defaultTheme],
    likedAirportsIata: [],
    musicVolume: DEFAULT_MUSIC_VOLUME,
    atcVolume: DEFAULT_ATC_VOLUME,
    atcStereoWidthMode: 'off',
    atcRadioEffectStrength: DEFAULT_ATC_RADIO_EFFECT_STRENGTH,
    streakCount: 0,
    lastVisitDate: null,
    sessionStartTime: null,
  } as UserPreferencesState,
  reducers: {
    setSelectedThemeName(state, action) {
      state.selectedThemeName = action.payload;
    },
    setThemes(state, action: PayloadAction<AppTheme[]>) {
      state.themes = action.payload;
    },
    setLikedAirports(state, action) {
      state.likedAirportsIata = action.payload;
    },
    setMusicVolume(state, action) {
      state.musicVolume = action.payload;
    },
    setAtcVolume(state, action) {
      state.atcVolume = action.payload;
    },
    setAtcStereoWidthMode(state, action: PayloadAction<AtcStereoWidthMode>) {
      state.atcStereoWidthMode = action.payload;
    },
    setAtcRadioEffectStrength(state, action: PayloadAction<number>) {
      const value = action.payload;
      state.atcRadioEffectStrength = Number.isFinite(value)
        ? Math.max(0, Math.min(100, Math.round(value)))
        : DEFAULT_ATC_RADIO_EFFECT_STRENGTH;
    },
    addLikedAirport(state, action) {
      state.likedAirportsIata.push(action.payload);
    },
    setStreakCount(state, action: PayloadAction<number>) {
      // Ensure the value is a valid number
      const count = action.payload;
      state.streakCount = isNaN(count) ? 0 : count;
    },
    setLastVisitDate(state, action: PayloadAction<string | null>) {
      state.lastVisitDate = action.payload;
    },
    setSessionStartTime(state, action: PayloadAction<number | null>) {
      state.sessionStartTime = action.payload;
    },
    resetStreak(state) {
      state.streakCount = 0;
      state.lastVisitDate = null;
      state.sessionStartTime = null; // Reset session start time as well
    },
    incrementStreak(state) {
      // Ensure we're incrementing a valid number
      const currentStreak = state.streakCount || 0;
      state.streakCount = currentStreak + 1;
    }
  },
  selectors: {
    getSelectedTheme: (state: UserPreferencesState) => {
      if (!state.themes || state.themes.length === 0) {
        console.warn("Themes are not initialized or empty. Falling back to default theme.");
        return defaultTheme;
      }
      return state.themes.find(theme => theme.name === state.selectedThemeName) || defaultTheme;
    },
    getLikedAirports: (state: UserPreferencesState) => state.likedAirportsIata,
    getMusicVolume: (state: UserPreferencesState) => state.musicVolume,
    getAtcVolume: (state: UserPreferencesState) => state.atcVolume,
    getAtcStereoWidthMode: (state: UserPreferencesState) => state.atcStereoWidthMode,
    getAtcRadioEffectStrength: (state: UserPreferencesState) => (
      state.atcRadioEffectStrength ?? DEFAULT_ATC_RADIO_EFFECT_STRENGTH
    ),
    getStreakCount: (state: UserPreferencesState) => {
      // Ensure we return a valid number
      return state.streakCount === undefined || state.streakCount === null || isNaN(state.streakCount)
        ? 0
        : state.streakCount;
    },
    getLastVisitDate: (state: UserPreferencesState) => state.lastVisitDate,
    getSessionStartTime: (state: UserPreferencesState) => state.sessionStartTime,
    getAllThemes: (state: UserPreferencesState) => state.themes,
  }
})

export const { getAllThemes, getSelectedTheme, getAtcVolume, getAtcStereoWidthMode, getAtcRadioEffectStrength, getLikedAirports, getMusicVolume, getStreakCount, getLastVisitDate, getSessionStartTime } = userPreferencesSlice.selectors;
export const { setThemes, setSelectedThemeName, setLikedAirports, setMusicVolume, setAtcVolume, setAtcStereoWidthMode, setAtcRadioEffectStrength, addLikedAirport, setStreakCount, setLastVisitDate, setSessionStartTime, resetStreak, incrementStreak } = userPreferencesSlice.actions;

export const startAppListening = listenerMiddleware.startListening

startAppListening({
  actionCreator: setSelectedThemeName,
  effect: async (action, listenerApi) => {
    try {
      const state = listenerApi.getState() as { userPreferences: UserPreferencesState };
      const selectedTheme = getSelectedTheme(state);

      Logger.info(`Applying theme: ${action.payload}`, 'theme');

      applyTheme(selectedTheme);
    } catch (error) {
      Logger.exception(error, 'Theme application', 'theme');
      // We could also dispatch an error action here using the new error handling system
    }
  }
})

startAppListening({
  actionCreator: setThemes,
  effect: async (_, listenerApi) => {
    try {
      const state = listenerApi.getState() as { userPreferences: UserPreferencesState };
      const selectedTheme = getSelectedTheme(state);

      Logger.info(`Applying theme after themes bootstrap: ${selectedTheme.name}`, 'theme');

      applyTheme(selectedTheme);
    } catch (error) {
      Logger.exception(error, 'Theme bootstrap application', 'theme');
    }
  }
})
